import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  ISubcategoriaIncidentes,
  ISubcategoriaIncidentesPagingRes,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useUiStore } from '@/store/ui';

import {
  USE_MOCK,
  MOCK_SUBCATEGORIA_INCIDENTES_PAGING,
  MOCK_SUBCATEGORIA_INCIDENTES,
  mockAxiosResponse,
} from '@/shared/actions/erp/tickets-soporte-cliente/tickets-mock-data';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchSubcategoriaIncidencia = ({
  page = 1,
  page_size = 10,
  nombre = '',
  numero_referencia,
  descripcion,
  categoria_incidencia,
  enabled = true,
}: GetSubcategoriaIncidenciaParams) => {
  return useQuery({
    queryKey: ['subcategoriaincidencia', page, nombre,numero_referencia,descripcion,categoria_incidencia, page_size],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_SUBCATEGORIA_INCIDENTES_PAGING);
      return getSubcategoriaIncidencias({ page, nombre,numero_referencia,descripcion,categoria_incidencia, page_size });
    },
    enabled
  });
};

export const useFetchSubcategoriaIncidenciaEnabled = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetSubcategoriaIncidenciaParams>) => {
  return useQuery({
    queryKey: ['subcategoriaincidencia', params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_SUBCATEGORIA_INCIDENTES_PAGING);
      return getSubcategoriaIncidencias(params);
    },
    enabled,
  });
};

export const useGetSubcategoriaIncidencia = (id: string) => {
  return useQuery({
    queryKey: ['subcategoriaincidencias', id],
    queryFn: async () => {
      if (USE_MOCK) {
        const item = MOCK_SUBCATEGORIA_INCIDENTES.find((s) => s.id_subcategoria_incidencia === id) || MOCK_SUBCATEGORIA_INCIDENTES[0];
        return mockAxiosResponse(item);
      }
      return getSubcategoriaIncidencia(id);
    },
  });
};

export const useCreateSubcategoriaIncidencia = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createSubcategoriaIncidencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcategoriaincidencia'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Subcategoría incidencia creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateSubcategoriaIncidencia = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateSubcategoriaIncidencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcategoriaincidencia'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ??
            'Subcategoría incidencia actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetSubcategoriaIncidenciaParams = {
  enabled?: boolean;
  page?: number;
  page_size?: number;
  nombre?: string;
  numero_referencia?:string;
  descripcion?:string;
  categoria_incidencia?:string;
};

export type CreateSubcategoriaIncidenciaParams = Omit<
  ISubcategoriaIncidentes,
  'id_subcategoria_incidencia'
>;
export type UpdateSubcategoriaIncidenciaParams = {
  id: string;
  data: CreateSubcategoriaIncidenciaParams;
};

export const getSubcategoriaIncidencias = async (
  params?: GetSubcategoriaIncidenciaParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ISubcategoriaIncidentesPagingRes>(
    `${urlBackend}api/v1/subcategoriaincidencia/?${queryParams}`,
    true
  );
};

export const getSubcategoriaIncidencia = async (id: string) => {
  try {
    return await get<ISubcategoriaIncidentes>(
      `${urlBackend}api/v1/subcategoriaincidencia/${id}/`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createSubcategoriaIncidencia = async (
  data: CreateSubcategoriaIncidenciaParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<ISubcategoriaIncidentes>(
    `${urlBackend}api/v1/subcategoriaincidencia/`,
    data,
    true
  );
};
export const updateSubcategoriaIncidencia = async ({
  id,
  data,
}: UpdateSubcategoriaIncidenciaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<ISubcategoriaIncidentes>(
    `${urlBackend}api/v1/subcategoriaincidencia/${id}/`,
    data,
    true
  );
};
